import React, { useState, useEffect } from 'react';
import {
    format,
    isSameDay,
    isAfter,
    isBefore,
    startOfMonth,
    endOfMonth,
    eachDayOfInterval,
    addMonths,
    subMonths,
    isToday,
    parse,
    isValid,
} from 'date-fns';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faChevronLeft, faCalendar } from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from 'react-i18next';
import { formatUiMonthYear } from '../helpers/DateTime';

const initDate = (startInput, endInput) => {
    if(endInput) {
        return endInput ;
    } else if (startInput) {
        return startInput;
    } else {
        return new Date();
    }
}

const DatePicker = ({ startDate, endDate, setStartDate, setEndDate }) => {
    const { t, i18n } = useTranslation();
    const [startInput, setStartInput] = useState(startDate ? format(startDate, 'yyyy-MM-dd') : ''); // For controlled input value
    const [endInput, setEndInput] = useState(endDate ? format(endDate, 'yyyy-MM-dd') : ''); // For controlled input value
    const [currentDate, setCurrentDate] = useState(initDate(startInput, endInput)); // Use startDate to initialize month view if available

    useEffect(() => {
        // Update the currentDate to the selected start date's month
        if (startDate) {
            setCurrentDate(initDate(startInput, endInput));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
    }, [startDate]);

    const handleDateClick = (date) => {
        if (!startDate || (startDate && endDate)) {
            setStartDate(date);
            setStartInput(format(date, 'yyyy-MM-dd')); // Update input field
            setEndDate(null);
            setEndInput(''); // Reset end input field
        } else if (isBefore(date, startDate)) {
            setStartDate(date);
            setStartInput(format(date, 'yyyy-MM-dd')); // Update input field
        } else {
            setEndDate(date);
            setEndInput(format(date, 'yyyy-MM-dd')); // Update input field
        }
    };

    const handlePrevMonth = () => {
        setCurrentDate(subMonths(currentDate, 1));
    };

    const handleNextMonth = () => {
        setCurrentDate(addMonths(currentDate, 1));
    };

    const isInRange = (date) => {
        return startDate && endDate && isAfter(date, startDate) && isBefore(date, endDate);
    };

    const isSelected = (date) => {
        return (startDate && isSameDay(date, startDate)) || (endDate && isSameDay(date, endDate));
    };

    const handleInputChange = (e, isStart = true) => {
        const value = e.target.value;
        if (isStart) {
            setStartInput(value);
        } else {
            setEndInput(value);
        }
    };

    const handleInputBlur = (isStart = true) => {
        if (isStart) {
            const parsedDate = parse(startInput, 'yyyy-MM-dd', new Date());
            if (!isValid(parsedDate)) {
                // Reset to last valid selected date
                setStartInput(startDate ? format(startDate, 'yyyy-MM-dd') : '');
            } else {
                setStartDate(parsedDate);
                if (endDate && isAfter(parsedDate, endDate)) setEndDate(null);
                // Adjust the calendar to the month of the selected date
                setCurrentDate(startOfMonth(parsedDate));
            }
        } else {
            const parsedDate = parse(endInput, 'yyyy-MM-dd', new Date());
            if (!isValid(parsedDate) || (startDate && isBefore(parsedDate, startDate))) {
                // Reset to last valid selected date
                setEndInput(endDate ? format(endDate, 'yyyy-MM-dd') : '');
            } else {
                setEndDate(parsedDate);
                // Adjust the calendar to the month of the selected date
                setCurrentDate(startOfMonth(parsedDate));
            }
        }
    };

    const generateCalendar = () => {
        const startOfCurrentMonth = startOfMonth(currentDate);
        const endOfCurrentMonth = endOfMonth(currentDate);
        const days = eachDayOfInterval({ start: startOfCurrentMonth, end: endOfCurrentMonth });
        const startDay = (startOfCurrentMonth.getDay() + 6) % 7

        return (
            <div className="grid grid-cols-7 gap-1 w-full min-w-0">
                {[1, 2, 3, 4, 5, 6, 0].map((day) => (
                    <div key={day} className="text-center font-normal text-sm text-mute-300">{t(`weekdayShort.${day}`)}</div>
                ))}
                {Array.from({ length: startDay }, (_, i) => <div key={i} />)}
                {days.map((date) => (
                    <div
                        key={date}
                        className={`p-2 text-center text-black text-sm font-normal cursor-pointer rounded-lg w-full min-w-0 h-8 flex justify-center items-center ${isSelected(date)
                                ? 'bg-primary-300'
                                : isInRange(date)
                                    ? 'bg-primary-100'
                                    : isToday(date)
                                        ? 'border border-primary-300' // Highlight today's date
                                        : 'hover:bg-primary-100'
                            }`}
                        onClick={() => handleDateClick(date)}
                    >
                        {date.getDate()}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="w-72 max-w-full p-4 rounded-lg text-black bg-white">
            <div className='flex flex-col'>
                <input
                    type="text"
                    value={startInput}
                    onChange={(e) => handleInputChange(e, true)}
                    onBlur={() => handleInputBlur(true)}
                    className="mb-2 input-default w-full max-w-full"
                    pattern="\d{4}-\d{2}-\d{2}"
                    placeholder={t("form.startDate")}
                />
                <FontAwesomeIcon icon={faCalendar} className='h-4 text-black absolute right-0 mr-8 mt-3' />
            </div>
            <div className='flex flex-col'>
                <input
                    type="text"
                    value={endInput}
                    onChange={(e) => handleInputChange(e, false)}
                    onBlur={() => handleInputBlur(false)}
                    className="mb-2 input-default w-full max-w-full"
                    pattern="\d{4}-\d{2}-\d{2}"
                    placeholder={t("form.endDate")}
                />
                <FontAwesomeIcon icon={faCalendar} className='h-4 text-black absolute right-0 mr-8 mt-3' />
            </div>
            <div className="flex items-center justify-between mb-2">
                <button onClick={handlePrevMonth} className="p-1">
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <div className="text-center text-sm font-normal">{formatUiMonthYear(currentDate, i18n.resolvedLanguage || i18n.language)}</div>
                <button onClick={handleNextMonth} className="p-1">
                    <FontAwesomeIcon icon={faChevronRight} />
                </button>
            </div>
            {generateCalendar()}
        </div>
    );
};

export default DatePicker;
